import React, { useState, useRef, useContext } from 'react'
import PropTypes from 'prop-types'
import { getSubjectTextClass } from '@helpers/subject'
import {
  HEADING_TYPE,
  BUTTON_TYPE,
  COLORS,
  TEXT_SIZES,
  BUTTON_SIZE,
  USER_ROLES,
  SCHOOL_RESOURCE_TYPES,
} from '@helpers/constants'
import Heading from '@components/Heading'
import Image from '@components/Image'
import Button from '@components/Button'
import Text from '@components/Text'
import Icon from '@components/Icon'
import { getBookImageUrl } from '@helpers/book'
import { useCurrentUser } from '@hooks/useCurrentUser'
import Tooltip from '@components/Tooltip'
import withTooltip from '@components/WithTooltip'
import {
  allSectionsCompleted,
  allSectionsPassed,
  anySectionHasExamAttempts,
  anySectionHasExams,
  anySectionHasFailedExam,
  anySectionHasQuizExams,
  getSectionTagIcon,
  StatusCorner,
} from '@helpers/exams'
import { AppContext } from '@state/context'
import { useConfirm } from '@hooks/useConfirm'
import BookProgressButtons from '@components/BookProgressButtons'

const ButtonWithTooltip = withTooltip(Button)

const StudentBookCardNew = (props) => {
  const { unShare, updateBookArchiveStatus, book, resourceType, resource } =
    props

  const { id, subject, schoolGroups, sections } = book
  const { currentUser } = useCurrentUser()
  const userRole = currentUser?.userRole

  const [state, dispatch] = useContext(AppContext)

  const {
    app: { selectedStudent },
  } = state

  const sharer = () => {
    if (
      resourceType === SCHOOL_RESOURCE_TYPES.DEMO_CLASS ||
      resourceType === SCHOOL_RESOURCE_TYPES.DEMO_GROUP
    ) {
      return currentUser
    } else {
      return book?.pivot?.sharedBy
    }
  }

  const pivotId = book?.pivot?.id
  const isArchived = book?.pivot?.is_archived

  // sharedBy is null when the teacher who shared the book has been deleted
  const sharedBy = sharer()
  const teacherName = sharedBy
    ? `${sharedBy.first_name} ${sharedBy.last_name}`
    : 'Borttagen lärare'
  const { name } = book
  const imageUrl = getBookImageUrl(book)
  const subjectColor = getSubjectTextClass(subject?.slug)

  let singleBookUrl = `/bok/${id}`

  if (userRole !== USER_ROLES.STUDENT) {
    if (resourceType === SCHOOL_RESOURCE_TYPES.CLASS) {
      singleBookUrl = `/klass/${resource.id}/bok/${id}`
    }
    if (resourceType === SCHOOL_RESOURCE_TYPES.GROUP) {
      singleBookUrl = `/grupp/${resource.id}/bok/${id}`
    }
  }

  const bookHasExams = anySectionHasExams(sections)
  const bookHasQuizExams = anySectionHasQuizExams(sections)

  return (
    <div className='relative rounded-[4px] bg-black flex flex-col justify-between'>
      {(userRole === USER_ROLES.STUDENT || selectedStudent) && (
        <StudentStatusCorner
          sections={sections}
          selectedStudent={selectedStudent}
        />
      )}

      <SchoolGroupsBanner schoolGroups={schoolGroups} />

      <div className='p-4 flex items-start justify-between'>
        <Text color={COLORS.WHITE.TEXT} size={TEXT_SIZES.XXSMALL}>
          {teacherName}
        </Text>

        {bookHasQuizExams && (
          <Icon
            icon={'ExamsBoard'}
            iconClasses='w-[32px] h-[32px]'
            color={COLORS.SECONDARY_GREEN.TEXT}
          />
        )}
      </div>

      <div className='p-4 self-center'>
        <div className={'h-[125px] w-[125px]'}>
          {imageUrl && <Image imageUrl={imageUrl} />}
        </div>
      </div>

      <div>
        <div className='p-4'>
          <div className={`${subjectColor} flex`}>
            <Icon icon={'Book'} />
            <Text
              size={TEXT_SIZES.XXXSMALL}
              color={subjectColor}
              extraClasses={'pl-2'}
            >
              {subject?.name}
            </Text>
          </div>

          <Heading
            size={TEXT_SIZES.MEDIUM}
            headingLevel={HEADING_TYPE.H2}
            color={COLORS.WHITE.TEXT}
            extraClasses='mb-2'
          >
            {name}
          </Heading>

          <div className='flex justify-start gap-2'>
            <Button
              size={BUTTON_SIZE.MEDIUM}
              type={BUTTON_TYPE.PRIMARY}
              to={singleBookUrl}
            >
              Öppna boken
            </Button>

            {userRole && userRole !== USER_ROLES.STUDENT && (
              <>
                <Button
                  size={BUTTON_SIZE.MEDIUM}
                  type={BUTTON_TYPE.DARK_GREY}
                  icon={isArchived ? 'UndoArrow' : 'FileCabinet'}
                  iconRight
                  handleClick={() => {
                    updateBookArchiveStatus(pivotId, !isArchived)
                  }}
                >
                  {isArchived ? 'Avarkivera' : 'Arkivera'}
                </Button>

                {isArchived && (
                  <UnShareButton
                    unShare={unShare}
                    resourceType={resourceType}
                    schoolGroups={schoolGroups}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Exams bar */}
      {bookHasExams && (
        <div className='bg-darkGrey p-4 rounded-b-[4px] min-h-[56px]'>
          {/* Student-view */}
          {userRole && userRole === USER_ROLES.STUDENT && (
            <div className='flex flex-wrap gap-1'>
              {sections.map((section, index) => {
                const { id: sectionId, exams } = section
                const icon = getSectionTagIcon(exams)

                return (
                  <Icon
                    key={`${index}-${sectionId}`}
                    icon={icon}
                    color={COLORS.WHITE.TEXT}
                  />
                )
              })}
            </div>
          )}

          {/* Teacher/Admin-view */}
          {userRole && userRole !== USER_ROLES.STUDENT && (
            <>
              {!selectedStudent ? (
                // --- Whole group details ---
                <>
                  <BookProgressButtons book={book} resource={resource} />
                </>
              ) : (
                // --- Selected student details ---
                <div className='flex flex-wrap gap-1'>
                  {sections.map((section, index) => {
                    const { id: sectionId, exams } = section
                    const icon = getSectionTagIcon(
                      exams,
                      'schoolClassAttempts',
                      [selectedStudent.id]
                    )

                    return (
                      <Icon
                        key={`${index}-${sectionId}`}
                        icon={icon}
                        color={COLORS.WHITE.TEXT}
                      />
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

StudentBookCardNew.propTypes = {
  book: PropTypes.object,
  resourceType: PropTypes.string,
}

// --- SubComponents

const StudentStatusCorner = (props) => {
  const { sections, selectedStudent } = props

  const bookHasExams = anySectionHasExams(sections)
  const bookHasAttempts = selectedStudent
    ? anySectionHasExamAttempts(sections, 'schoolClassAttempts', [
        selectedStudent.id,
      ])
    : anySectionHasExamAttempts(sections)
  const bookIsPassed = selectedStudent
    ? allSectionsPassed(sections, 'schoolClassAttempts', [selectedStudent.id])
    : allSectionsPassed(sections)
  const bookIsFailed = selectedStudent
    ? anySectionHasFailedExam(sections, 'schoolClassAttempts', [
        selectedStudent.id,
      ])
    : anySectionHasFailedExam(sections)
  const bookIsCompleted = selectedStudent
    ? allSectionsCompleted(sections, 'schoolClassAttempts', [
        selectedStudent.id,
      ])
    : allSectionsCompleted(sections)

  if (!bookHasExams) return null

  if (bookIsPassed) {
    return <StatusCorner passed={true} size='small' />
  } else {
    if (bookIsCompleted) {
      if (bookIsFailed) {
        return <StatusCorner passed={false} size='small' />
      }
    } else {
      if (bookHasAttempts) {
        return <StatusCorner size='small' />
      }
    }
  }

  return null
}

const SchoolGroupsBanner = (props) => {
  const { schoolGroups } = props

  const [groupsButtonRef, setGroupsButtonRef] = useState()
  const [showGroupsTooltip, setShowGroupsTooltip] = useState()

  if (!schoolGroups) return null
  if (schoolGroups.length === 0) return null

  if (schoolGroups.length === 1) {
    return (
      <div className={'bg-candyRed absolute top-[6px] right-[-6px] py-2 px-4'}>
        <p className='text-white text-xxSmall w-min max-w-[5rem] text-ellipsis overflow-hidden'>
          {schoolGroups[0].name}
        </p>
      </div>
    )
  }

  if (schoolGroups.length > 1) {
    return (
      <>
        <div
          className={'absolute top-[6px] right-[-6px]'}
          onMouseOver={() => setShowGroupsTooltip(true)}
          onMouseLeave={() => setShowGroupsTooltip(false)}
        >
          <Button
            ref={setGroupsButtonRef}
            backgroundColor={COLORS.CANDY_RED.BACKGROUND}
            textColor={COLORS.WHITE.TEXT}
            size={BUTTON_SIZE.MEDIUM}
            active={showGroupsTooltip}
            classes={'self-center'}
          >
            {schoolGroups.length} grupper
          </Button>
        </div>
        <Tooltip
          show={showGroupsTooltip}
          referenceElement={groupsButtonRef}
          onHide={() => setShowGroupsTooltip(false)}
        >
          {schoolGroups.map((schoolGroup) => (
            <p key={schoolGroup.id} className={COLORS.WHITE.TEXT}>
              {schoolGroup.name}
            </p>
          ))}
        </Tooltip>
      </>
    )
  }

  return null
}

const UnShareButton = (props) => {
  const { unShare, resourceType, schoolGroups } = props

  const shareButtonRef = useRef()
  const [showShareTooltip, setShowShareTooltip] = useState(false)

  const { confirm } = useConfirm()

  const confirmUnShare = async () => {
    const confirmed = await confirm({
      // title: 'Lorem Ipsum',
      text: 'Är du säker på att du vill sluta dela boken?',
      confirmText: 'Ja, sluta dela',
      cancelText: 'Avbryt',
    })

    if (confirmed) {
      unShare()
    }
  }

  return (
    <div
      ref={shareButtonRef}
      onMouseOver={() => setShowShareTooltip(true)}
      onMouseLeave={() => setShowShareTooltip(false)}
    >
      <ButtonWithTooltip
        type={BUTTON_TYPE.DARK_GREY}
        size={BUTTON_SIZE.ICON_CIRCLE}
        handleClick={() => {
          confirmUnShare()
        }}
        icon={'Exit'}
        disabled={!!schoolGroups}
        tooltip={
          schoolGroups
            ? undefined
            : `Sluta dela boken med denna ${
                resourceType === SCHOOL_RESOURCE_TYPES.CLASS ? 'klass' : 'grupp'
              }.`
        }
      />
      {schoolGroups && (
        <Tooltip
          referenceElement={shareButtonRef.current}
          show={showShareTooltip}
        >
          <Text color={COLORS.WHITE.TEXT} noPadding>
            Du kan bara sluta dela den här boken via gruppen.
          </Text>
        </Tooltip>
      )}
    </div>
  )
}

export default StudentBookCardNew
